import { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, Plug, Plus, Trash2, RotateCcw, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TEMPLATES = [
  { key: "speaker", title: "Template 1 — Pengingat ke Pemateri", desc: "Dikirim otomatis H-1 pukul 17:00 WIB ke nomor pemateri/petugas." },
  { key: "confirmed", title: "Template 2 — Info Agenda ke Grup", desc: "Dikirim saat status kegiatan menjadi Terkonfirmasi." },
  { key: "finished", title: "Template 3 — Laporan Selesai ke Grup", desc: "Dikirim saat status kegiatan menjadi Selesai." },
  { key: "cancelled", title: "Template 4 — Pemberitahuan Dibatalkan ke Grup", desc: "Dikirim saat status kegiatan menjadi Dibatalkan." },
];
const KIND_LABEL = { pengingat_pemateri: "Pengingat Pemateri", agenda_grup: "Info Agenda (Grup)", selesai_grup: "Laporan Selesai (Grup)", batal_grup: "Pembatalan (Grup)", donasi_admin: "Donasi → Admin", donasi_donatur: "Donasi → Donatur", lainnya: "Lainnya" };

const Card = ({ title, desc, children, testId }) => (
  <section className="rounded-3xl bg-white border border-emerald-900/10 p-6 space-y-4" data-testid={testId}>
    <div><h3 className="font-semibold text-emerald-950">{title}</h3>{desc && <p className="text-sm text-slate-500 mt-1">{desc}</p>}</div>
    {children}
  </section>
);

export default function PengaturanWhatsApp() {
  const [s, setS] = useState(null);
  const [token, setToken] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState("");
  const [logs, setLogs] = useState([]);

  const load = () => Promise.all([api.get("/whatsapp/settings"), api.get("/whatsapp/logs")]).then(([a, b]) => { setS(a.data); setLogs(b.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/whatsapp/settings", { fonnte_token: token, wa_groups: s.wa_groups, wa_templates: s.wa_templates });
      setS(data); setToken("");
      toast.success("Pengaturan WhatsApp disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/whatsapp/test", { fonnte_token: token });
      setS((p) => ({ ...p, fonnte_status: data }));
      toast[data.connected ? "success" : "warning"](data.connected ? `Terhubung · ${data.device}` : "Token valid, tetapi device Fonnte belum terhubung");
    } catch (e) {
      toast.error(errMsg(e));
      setS((p) => ({ ...p, fonnte_status: { connected: false, reason: errMsg(e), checked_at: new Date().toISOString() } }));
    } finally { setBusy(""); }
  };

  if (!s) return <p className="text-slate-500">Memuat...</p>;
  const st = s.fonnte_status || {};
  const setGroup = (i, k, v) => setS((p) => ({ ...p, wa_groups: p.wa_groups.map((g, idx) => (idx === i ? { ...g, [k]: v } : g)) }));

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-page">
      <PageHeader title="Pengaturan WhatsApp" subtitle="Koneksi Fonnte, nomor tujuan grup, template pesan, dan riwayat pengiriman">
        <Button onClick={save} disabled={!!busy} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid="wa-save-btn">{busy === "save" ? <Loader2 size={16} className="animate-spin" /> : "Simpan Pengaturan"}</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Koneksi Fonnte" desc="Token dari dashboard md.fonnte.com → Device → Token. Disimpan terenkripsi." testId="wa-connection">
          <div className="space-y-1.5">
            <Label htmlFor="fonnte">Token Fonnte {s.has_fonnte_token && <span className="text-xs font-normal text-emerald-700" data-testid="wa-token-saved">(sudah tersimpan — kosongkan jika tidak diubah)</span>}</Label>
            <div className="relative">
              <Input id="fonnte" type={show ? "text" : "password"} value={token} onChange={(e) => setToken(e.target.value)} placeholder="Tempel token Fonnte" className="pr-10" data-testid="wa-token-input" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-800" data-testid="wa-token-toggle">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" className="rounded-full" onClick={test} disabled={!!busy} data-testid="wa-test-btn">{busy === "test" ? <Loader2 size={16} className="animate-spin" /> : <><Plug size={15} className="mr-1" /> Tes Koneksi</>}</Button>
            <div className="flex items-center gap-2" data-testid="wa-status">
              {st.connected ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><Wifi size={12} className="mr-1" /> Terhubung</Badge> : <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><WifiOff size={12} className="mr-1" /> Tidak Terhubung</Badge>}
              {st.device && <span className="text-sm text-slate-600" data-testid="wa-device">Pengirim: <b>+{st.device}</b>{st.name ? ` (${st.name})` : ""}</span>}
            </div>
          </div>
          {st.checked_at && <p className="text-xs text-slate-400">Terakhir dites: {st.checked_at.slice(0, 16).replace("T", " ")}{st.reason && !st.connected ? ` · ${st.reason}` : ""}{st.quota ? ` · Kuota: ${st.quota}` : ""}</p>}
        </Card>

        <Card title="Nomor Tujuan (Grup WA)" desc="Format: 628xxxxxxxx-xxxx@g.us atau 1203630xxxx@g.us. Dapatkan dari Fonnte → Device → Get Group ID. Semua grup akan menerima pesan." testId="wa-groups">
          <div className="space-y-2">
            {s.wa_groups.map((g, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`wa-group-row-${i}`}>
                <Input className="h-9 flex-[2]" placeholder="ID grup (…@g.us)" value={g.id} onChange={(e) => setGroup(i, "id", e.target.value)} data-testid={`wa-group-id-${i}`} />
                <Input className="h-9 flex-1" placeholder="Nama grup" value={g.name || ""} onChange={(e) => setGroup(i, "name", e.target.value)} data-testid={`wa-group-name-${i}`} />
                <button type="button" onClick={() => setS((p) => ({ ...p, wa_groups: p.wa_groups.filter((_, idx) => idx !== i) }))} className="text-slate-400 hover:text-red-600" data-testid={`wa-group-remove-${i}`}><Trash2 size={15} /></button>
              </div>
            ))}
            {s.wa_groups.length === 0 && <p className="text-sm text-slate-400" data-testid="wa-groups-empty">Belum ada grup tujuan.</p>}
          </div>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setS((p) => ({ ...p, wa_groups: [...p.wa_groups, { id: "", name: "" }] }))} data-testid="wa-group-add-btn"><Plus size={14} className="mr-1" /> Tambah Grup</Button>
        </Card>
      </div>

      <Card title="Template Pesan" desc="Gunakan variabel dalam kurung kurawal ganda; sistem akan menggantinya otomatis saat mengirim." testId="wa-templates">
        <div className="grid lg:grid-cols-2 gap-5">
          {TEMPLATES.map((t) => (
            <div key={t.key} className="rounded-2xl border border-emerald-900/10 p-4 space-y-2" data-testid={`wa-template-${t.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-semibold text-sm text-emerald-950">{t.title}</p><p className="text-xs text-slate-500">{t.desc}</p></div>
                <button type="button" title="Kembalikan ke bawaan" onClick={() => setS((p) => ({ ...p, wa_templates: { ...p.wa_templates, [t.key]: p.default_templates[t.key] } }))} className="text-slate-400 hover:text-emerald-800" data-testid={`wa-template-reset-${t.key}`}><RotateCcw size={14} /></button>
              </div>
              <Textarea rows={9} value={s.wa_templates[t.key] || ""} onChange={(e) => setS((p) => ({ ...p, wa_templates: { ...p.wa_templates, [t.key]: e.target.value } }))} className="font-mono text-xs" data-testid={`wa-template-input-${t.key}`} />
              <div className="flex flex-wrap gap-1.5">
                {s.variables.map((v) => <code key={v} className="rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 text-[11px]">{`{{${v}}}`}</code>)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Riwayat Pengiriman" desc="200 pesan WhatsApp terakhir yang dikirim sistem." testId="wa-logs">
        <div className="overflow-x-auto -mx-6">
          <Table data-testid="wa-logs-table">
            <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Jenis Pesan</TableHead><TableHead>Kegiatan / Konteks</TableHead><TableHead>Tujuan</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400" data-testid="wa-logs-empty">Belum ada pengiriman.</TableCell></TableRow>}
              {logs.map((l) => (
                <TableRow key={l.id} data-testid={`wa-log-${l.id}`}>
                  <TableCell className="whitespace-nowrap text-slate-600">{l.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell>{KIND_LABEL[l.kind] || l.kind}</TableCell>
                  <TableCell className="max-w-xs truncate">{l.activity_title || "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{l.target}</TableCell>
                  <TableCell>{l.ok ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Terkirim</Badge> : <Badge className="bg-red-100 text-red-800 hover:bg-red-100" title={l.reason}>Gagal{l.reason ? ` · ${l.reason}` : ""}</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
